import Cookies from 'js-cookie'
import axios from 'axios'
import { captureException } from '@sentry/nextjs'
import flowMonitor from './flowMonitor'

export function getVersion() {
  const version = Cookies.get('insta_app_version') ?? '0.0.0'
  return version.replace('_dev_debug', '')
}

export function getToken() {
  const token = Cookies.get('insta_access_token')
  return token || ''
}

export function getLocalId() {
  return Cookies.get('insta_app_local_id') || Cookies.get('local_id')
}

export function getAccountId() {
  return Cookies.get('insta_user_id')
}

export function getAppDomain() {
  return Cookies.get('insta_app_domain')
}

export function debounce(idle, action, immediate = false) {
  var last
  return function () {
    var ctx = this,
      args = arguments
    if (immediate) {
      if (last) return
      action.apply(ctx, args)
      last = setTimeout(() => {
        immediate = false
      }, idle)
    } else {
      clearTimeout(last)
      last = setTimeout(function () {
        action.apply(ctx, args)
      }, idle)
    }
  }
}

export function throttle(wait, fn) {
  let pre = 0
  return function () {
    const now = new Date().getTime()
    if (now - pre >= wait) {
      fn.apply(this, arguments)
      pre = new Date().getTime()
    }
  }
}

export function sendException({ name, exception, message, data }, { fingerprint, contexts, tags } = {}) {
  try {
    const isBrowser = typeof window !== 'undefined'
    const isNetWork = exception?.message === 'Network Error'
    if (isBrowser && window.isDev) console.log('[sendException]: ', name, message)
    if (isNetWork) return
    const e = exception || new Error()
    e.name = name
    e.message = message || e.message
    const flowLogs = flowMonitor.dump()
    const finalData = { ...(data || {}) }
    const accountId = isBrowser ? getAccountId() : ''
    if (flowLogs.length) finalData.flowLogs = flowLogs
    captureException(e, {
      extra: {
        // 仅在隐藏态时附带 visible，平时无诊断价值
        ...(typeof document !== 'undefined' && document.visibilityState === 'hidden' ? { visible: 'hidden' } : {}),
        ...finalData,
      },
      tags: {
        ...(tags || {}),
        'insta:level': 'fatal',
      },
      contexts: {
        // Sentry contexts 值必须是对象，旧写法会被 Sentry 丢弃（"expected an object"）
        ...(accountId ? { 'insta:account': { id: String(accountId) } } : {}),
        ...(contexts || {}),
      },
      fingerprint,
    })
  } catch (error) {
    console.log('[sendException]: ', error)
  }
}

function getTokenUserRegion() {
  try {
    const token = getToken()
    if (token) {
      const [, encoded] = token.split('.')
      const decoded = window.atob(encoded)
      const userRegion = JSON.parse(decoded).userRegion
      if (userRegion === 1 || userRegion === 2) {
        return userRegion
      }
      return null
    }
    return null
  } catch {
    return null
  }
}

// ================= 根据 token 查询用户区域（临时方案）=================

export function requestWithRetry(requestFn, maxRetries = 3, retryDelay = 300) {
  const self = this
  return async function () {
    try {
      return await requestFn.apply(self, arguments)
    } catch (error) {
      const is404 = error?.isAxiosError && error?.response?.status === 404
      if (maxRetries > 0 && !is404) {
        await new Promise((resolve) => setTimeout(resolve, retryDelay))
        return requestWithRetry(requestFn, maxRetries - 1, retryDelay).apply(self, arguments)
      } else {
        console.log(`request failed, retry limit: ${maxRetries}`, error)
        throw error
      }
    }
  }
}

let ajaxUserRegion = null

async function fetchUserRegion() {
  if (getToken() && !getTokenUserRegion()) {
    const host = window.isCN ? window.SERVICE_DOMAIN_EXTERNAL_CN : window.SERVICE_DOMAIN_EXTERNAL_GLOBAL
    return axios
      .get(`${host}/infr-user-service/account/v1/getUserRegionProfileByToken`, {
        headers: {
          'X-User-Token': getToken(),
        },
      })
      .then((res) => {
        if (res.status === 200 && res.data.code === 0) {
          ajaxUserRegion = res.data?.data?.dataRegion
          console.log(`getUserRegionProfileByToken, region: ${ajaxUserRegion}`, typeof ajaxUserRegion)
          return ajaxUserRegion
        }
        return Promise.reject(res)
      })
  }
  return Promise.resolve(ajaxUserRegion)
}

/**
 * 获取用户服务器区域
 * @param {*} req
 * @param {*} isCN
 * @returns 1: 国内 2: 国外
 */
export function getUserServerRegion(req, isCN) {
  try {
    const token = req.cookies['insta_access_token']
    if (token) {
      const [, encoded] = token.split('.')
      const decoded = Buffer.from(encoded, 'base64').toString()
      const userRegion = JSON.parse(decoded).userRegion
      if (userRegion === 1 || userRegion === 2) {
        return userRegion
      }
    }
  } catch (e) {
    console.error('getUserServerRegion error:', e)
  }
  return isCN ? 1 : 2
}

export const fetchTokenRegionPromise = requestWithRetry(fetchUserRegion)().catch(() => {
  // 兜底走当前区域接口
  ajaxUserRegion = window.isCN ? 1 : 2
  console.log(`fetchUserRegion error, ajaxUserRegion: ${ajaxUserRegion}`)
  return ajaxUserRegion
})

// ================= 根据 token 查询用户区域（临时方案）=================

export function getUserRegion() {
  return ajaxUserRegion ?? getTokenUserRegion()
}

export function loadABTest({ id, defaultValue, timeout = 5000 }) {
  if (!window.collectEvent) {
    return Promise.resolve(defaultValue)
  }
  return new Promise((resolve) => {
    let flag = false
    window.collectEvent('getVar', id, defaultValue, (value) => {
      flag = true
      console.log('collectEvent', id, value)
      resolve(value)
    })
    // if timeout, return default value
    setTimeout(() => {
      if (!flag) {
        resolve(defaultValue)
      }
    }, timeout)
  })
}

export function compareAppVersions(version1, version2) {
  const arr1 = version1.split('.')
  const arr2 = version2.split('.')
  const maxLength = Math.max(arr1.length, arr2.length)
  const paddedArr1 = arr1.concat(new Array(maxLength - arr1.length).fill('0'))
  const paddedArr2 = arr2.concat(new Array(maxLength - arr2.length).fill('0'))

  for (let i = 0; i < maxLength; i++) {
    const num1 = parseInt(paddedArr1[i])
    const num2 = parseInt(paddedArr2[i])
    if (num1 < num2) {
      return -1 // version1 < version2
    } else if (num1 > num2) {
      return 1 // version1 > version2
    }
  }
  return 0 // version1 = version2
}
